import api from './api';
import { configService } from './ConfigService';

interface PaymentResponse {
  success: boolean;
  payment?: {
    id: string;
    transaction: {
      url: string;
    };
  };
  paymentUrl?: string;
  sessionId?: string;
  message?: string;
}

interface GeideaPaymentResponse {
  success: boolean;
  paymentUrl?: string;
  sessionId?: string;
  orderId?: string;
  message?: string;
}

class PaymentService {
  // Create Geidea payment session
  async createGeideaPayment(bookingId: string, email?: string, phone?: string): Promise<GeideaPaymentResponse> {
    try {
      console.log('🔄 Creating Geidea payment session...');
      
      const response = await api.post('/api/geidea/create-payment', {
        bookingId,
        email,
        phone
      });
      
      if (response.data.success && response.data.paymentUrl) {
        console.log('✅ Geidea payment session created');
        return {
          success: true,
          paymentUrl: response.data.paymentUrl,
          sessionId: response.data.sessionId,
          orderId: response.data.orderId
        };
      } else {
        throw new Error(response.data.message || 'فشل إنشاء جلسة الدفع');
      }
    } catch (error: any) {
      console.error('❌ Geidea payment error:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'حدث خطأ في إنشاء جلسة الدفع'
      };
    }
  }

  // Start Geidea checkout using JavaScript integration
  startGeideaCheckout(sessionId: string): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!window.GeideaCheckout) {
        reject(new Error('Geidea Checkout library not loaded'));
        return;
      }

      const onSuccess = (data: any) => {
        console.log('✅ Payment successful:', data);
        resolve({
          success: true,
          data
        });
      };

      const onError = (data: any) => {
        console.error('❌ Payment error:', data);
        resolve({
          success: false,
          error: data
        });
      };

      const onCancel = (data: any) => {
        console.log('⚠️ Payment cancelled:', data);
        resolve({
          success: false,
          cancelled: true,
          data
        });
      };

      try {
        const payment = new window.GeideaCheckout(onSuccess, onError, onCancel);
        payment.startPayment(sessionId);
      } catch (error) {
        reject(error);
      }
    });
  }
  // Create Geidea payment for additional tickets
  async createAdditionalTicketsPayment(
    bookingId: string,
    ticketCount: number,
    email?: string
  ): Promise<GeideaPaymentResponse> {
    try {
      console.log('🔄 Creating Geidea payment for additional tickets...');

      const response = await api.post('/api/geidea/create-additional-payment', {
        bookingId,
        ticketCount,
        email
      });

      if (response.data.success && response.data.payment?.transaction?.url) {
        console.log('✅ Geidea additional payment session created');
        return {
          success: true,
          paymentUrl: response.data.payment.transaction.url,
          sessionId: response.data.payment.id,
          orderId: response.data.payment.orderId
        };
      } else {
        throw new Error(response.data.message || 'فشل إنشاء جلسة الدفع الإضافية');
      }
    } catch (error: any) {
      console.error('❌ Additional Geidea payment error:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'حدث خطأ في إنشاء جلسة الدفع الإضافية'
      };
    }
  }

  // Create Tamara checkout session
  async createTamaraCheckout(bookingId: string, orderData: any): Promise<any> {
    try {
      console.log('🔄 Creating Tamara checkout session...');

      const response = await api.post('/api/tamara/create-checkout', {
        bookingId,
        orderData
      });

      if (response.data.success && response.data.checkoutUrl) {
        console.log('✅ Tamara checkout session created');
        return {
          success: true,
          checkoutUrl: response.data.checkoutUrl,
          orderId: response.data.orderId,
          checkoutId: response.data.checkoutId
        };
      } else {
        throw new Error(response.data.message || 'فشل إنشاء جلسة الدفع بالتقسيط');
      }
    } catch (error: any) {
      console.error('❌ Tamara checkout error:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'حدث خطأ في إنشاء جلسة الدفع بالتقسيط'
      };
    }
  }

  // Verify Tamara payment status
  async verifyTamaraPayment(orderId: string): Promise<any> {
    try {
      console.log('🔄 Verifying Tamara payment status...');

      const response = await api.post('/api/tamara/verify-payment', {
        orderId
      });

      if (response.data.success) {
        console.log('✅ Tamara payment verification successful');
        return {
          success: true,
          status: response.data.status,
          data: response.data.orderDetails
        };
      } else {
        throw new Error(response.data.message || 'فشل التحقق من حالة الدفع');
      }
    } catch (error: any) {
      console.error('❌ Tamara verification error:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'حدث خطأ في التحقق من حالة الدفع'
      };
    }
  }

  // Authorize Tamara order
  async authorizeTamaraOrder(orderId: string): Promise<any> {
    try {
      console.log('🔄 Authorizing Tamara order...');

      const response = await api.post('/api/tamara/authorize', {
        orderId
      });

      if (response.data.success) {
        console.log('✅ Tamara order authorized');
        return {
          success: true,
          status: response.data.status,
          captureId: response.data.captureId
        };
      } else {
        throw new Error(response.data.message || 'فشل تأكيد الطلب');
      }
    } catch (error: any) {
      console.error('❌ Tamara authorization error:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'حدث خطأ في تأكيد الطلب'
      };
    }
  }
}

export const paymentService = new PaymentService();